import React, { useState, useEffect, useContext, useRef } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useNotify } from 'react-admin';
import moment from 'moment';
import { Spin } from 'antd';
import _ from 'lodash';
import { getZ3PConfigValue, convertTime } from '../../helper';
import { apiGet } from '../../brainApi';
import { QuickAppointmentContext } from '../../Context';
import {
  getAllUniqueCompatibleDates,
  COLOR_LEGEND,
  renderTimeZone,
  setViewVariables,
  updatePropertiesHelper,
  getAndFormatAppointment,
  RenderSlotLaneTime,
  timeSlotLineStyle,
  calcTimeSlotTimes,
  borderColor,
  isTimeWithinAvailabilityRange,
  isOnStartTimeSlot,
} from '../../Dispatch/RescheduleModal';
import CalendarAppointment from '../../Dispatch/CalendarAppointment';
import {
  useCalendarStyles,
  renderLegend,
} from '../../Dispatch/FullCalendarHelper';

const useStyles = makeStyles(() => ({
  ...timeSlotLineStyle,
}));

const TimeSlotCalendar = ({
  record,
  selectedDate,
  setSelectedDate,
  serviceVehicleList,
}) => {
  const calendarClasses = useCalendarStyles();
  const notify = useNotify();
  const theme = useTheme();
  const classes = useStyles();
  const calendarRef = useRef();
  const lastDispatchInfoPromise = useRef();

  const {
    scheduleData,
    setScheduleData,
    contactData,
    setSelectedTimeSlot,
    onRruleChange,
    availableDatesToShow,
    setAvailableDatesToShow,
    isRecurring,
    isLoadingAdditionalDates,
    setIsLoadingAdditionalDates,
    rruleAttributes,
    conflictReason,
    setConflictReason,
  } = useContext(QuickAppointmentContext);

  const {
    service_offering_ids,
    home_address_id,
    client_location_id,
    total_labor_hours,
    location_type,
  } = record;

  const initialEvent = {
    id: 'new',
    title: `${contactData.first_name} ${contactData.last_name}`,
    display: 'auto',
    backgroundColor: 'white',
    borderColor: theme.palette.info.dark,
    textColor: theme.palette.info.dark,
    constraint: 'availableForAppointment',
    startEditable: false,
  };

  const timeRangeMin = convertTime(
    getZ3PConfigValue('time_range_min'),
    'militaryString',
  );
  const { is_using_time_blocks } = getZ3PConfigValue();

  const [viewRange, setViewRange] = useState(['08:00:00', '19:00:00']);
  const [dispatchInfo, setDispatchInfo] = useState([]);
  const [timeZone, setTimeZone] = useState('America/New_York');
  const [events, setEvents] = useState([initialEvent]);
  const [isLoadingCompatibleDates, setIsLoadingCompatibleDates] = useState(
    false,
  );
  const [isLoadingDispatchInfo, setIsLoadingDispatchInfo] = useState(false);
  const [
    isLoadingAdditionalDispatchInfo,
    setIsLoadingAdditionalDispatchInfo,
  ] = useState(false);
  const [allCompatibleDates, setAllCompatibleDates] = useState([]);
  const [availableTimeEvents, setAvailableTimeEvents] = useState([]);
  const [startTimeSlots, setStartTimeSlots] = useState([]);
  const [timeIsUnavailable, setTimeIsUnavailable] = useState(false);
  const [
    latestDateFetchedForRecurring,
    setLatestDateFetchedForRecurring,
  ] = useState(moment().format('YYYY-MM-DD'));

  const renderAppointmentEvents = () => {
    if (!dispatchInfo.length) {
      return [];
    }
    const schedules = dispatchInfo.filter(
      (schedule) =>
        scheduleData?.service_vehicle_id === schedule.service_vehicle_id,
    );
    const newAppointmentEvent = events.find((e) => e.id === 'new');
    const allEvents = newAppointmentEvent ? [newAppointmentEvent] : [];

    schedules.forEach((schedule) => {
      const activeAppointments = schedule.appointments.filter(
        (appt) => appt.status !== 'cancelled' && appt.status !== 'suggested',
      );
      const appointments = activeAppointments.map((appointment) => {
        const {
          scheduled_work_id,
          drive_start_time_utc,
          buffer_end_time_utc,
          customer,
        } = appointment;
        return {
          id: scheduled_work_id,
          backgroundColor: '#6D9ECA',
          borderColor: '#6D9ECA',
          start: drive_start_time_utc,
          end: buffer_end_time_utc,
          title: `${customer.first_name} ${customer.last_name}`,
          display: 'auto',
        };
      });
      allEvents.push(appointments);
    });
    setEvents(allEvents.flat());
  };

  // When user changes service vehicle or date range, filter all appointment events to match the current view.
  useEffect(() => {
    renderAppointmentEvents();
  }, [dispatchInfo, scheduleData?.service_vehicle_id, timeIsUnavailable]);

  // When user changes service vehicle autoselect the first available datetime
  useEffect(() => {
    if (!isRecurring) {
      const visibleStartDate = calendarRef.current.getApi().view.activeStart;
      const visibleEndDate = calendarRef.current.getApi().view.activeEnd;
      selectFirstAvailableTime(
        moment(visibleStartDate),
        moment(visibleEndDate),
      );
    }
  }, [availableDatesToShow, isRecurring]);

  const getCompatibleDates = (startDate = null, endDate = null) => {
    const params = {
      use_new_version: true,
      requested_service_offering_ids: service_offering_ids,
      total_labor_hours,
    };
    if (client_location_id) {
      params.client_location_id = client_location_id;
    } else {
      params.home_address_id = home_address_id;
    }
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    }

    apiGet('/get-compatible-dates', params)
      .then((res) => {
        if (res) {
          const dates = Object.keys(res).sort();
          if (dates.length > 0) {
            const allUniqueCompatibleDates = getAllUniqueCompatibleDates(
              res,
              dates,
              allCompatibleDates,
            );
            setAllCompatibleDates(allUniqueCompatibleDates);
          }
        }
      })
      .catch((error) => {
        console.log('Error: ', error);
        notify('Failed to find available dates', 'warning');
      })
      .finally(() => {
        setIsLoadingCompatibleDates(false);
        setIsLoadingAdditionalDates(false);
      });
  };

  // After loading compatible dates, auto-select first available date & service vehicle
  useEffect(() => {
    if (
      !isLoadingCompatibleDates &&
      !isRecurring &&
      serviceVehicleList.length
    ) {
      const firstAvailableDate = allCompatibleDates.find(
        (d) => d.is_available && d.available_start_times?.length > 0,
      )?.date;
      if (firstAvailableDate) {
        const allAvailableSchedules = allCompatibleDates.filter(
          (d) =>
            d.date === firstAvailableDate &&
            d.is_available &&
            d.available_start_times.length > 0,
        );
        const scheduleWithEarliestTime = allAvailableSchedules.reduce(
          (prev, current) => {
            return moment(prev.available_start_times[0]).isBefore(
              moment(current.available_start_times[0]),
            )
              ? prev
              : current;
          },
        );
        if (scheduleWithEarliestTime) {
          calendarRef.current.getApi().gotoDate(firstAvailableDate);
          validateTimeOnSchedule(
            scheduleWithEarliestTime,
            moment(scheduleWithEarliestTime.available_start_times[0]),
          );
          setScheduleData((prev) => {
            return {
              ...prev,
              service_vehicle_id: scheduleWithEarliestTime.service_vehicle_id,
              service_vehicle_name:
                scheduleWithEarliestTime.service_vehicle_name,
            };
          });
        }
      } else if (serviceVehicleList.length > 0) {
        setScheduleData((prev) => ({
          ...prev,
          service_vehicle_id: serviceVehicleList[0].service_vehicle_id,
          service_vehicle_name: serviceVehicleList[0].service_vehicle_name,
        }));
      }
    }
  }, [serviceVehicleList, isLoadingCompatibleDates, isRecurring]);

  useEffect(() => {
    // Only show loading indicator on initial load
    setIsLoadingCompatibleDates(true);
    setIsLoadingDispatchInfo(true);
  }, []);

  // Load all dates until the end of recurrence, if not already loaded
  useEffect(() => {
    const end = moment(rruleAttributes?.until).format('YYYY-MM-DD');
    if (
      isRecurring &&
      moment(latestDateFetchedForRecurring).isBefore(moment(end))
    ) {
      // We need to add the full spinner indicator so that the user doesn't navigate while dates are loading
      setIsLoadingCompatibleDates(true);
      setIsLoadingAdditionalDates(true);
      setLatestDateFetchedForRecurring(end);
      getCompatibleDates(latestDateFetchedForRecurring, end);
    }
  }, [isRecurring, rruleAttributes?.until]);

  const selectFirstAvailableTime = (min, max) => {
    const firstAvailableSlotTime = (firstAvailableDate) => {
      return firstAvailableDate.available_start_times.find((d) => {
        const availableTime = moment(d)
          .tz(timeZone)
          .format('HH:mm:00');
        const isAvailableTimeASlotTime = firstAvailableDate.predetermined_time_slots.find(
          (time) => time === availableTime,
        );
        if (isAvailableTimeASlotTime) {
          return availableTime;
        }
      });
    };
    if (availableDatesToShow?.length > 0 && min && max) {
      let firstAvailableDate = null;
      if (is_using_time_blocks) {
        firstAvailableDate = availableDatesToShow?.find(
          (d) =>
            moment(d.date).isSameOrAfter(min) &&
            moment(d.date).isSameOrBefore(max) &&
            d.is_available &&
            d.available_start_times.length > 0 &&
            d.predetermined_time_slots.length > 0 &&
            firstAvailableSlotTime(d)?.length,
        );
        if (firstAvailableDate) {
          return selectTime(moment(firstAvailableSlotTime(firstAvailableDate)));
        }
      }
      firstAvailableDate = availableDatesToShow?.find(
        (d) =>
          moment(d.date).isSameOrAfter(min) &&
          moment(d.date).isSameOrBefore(max) &&
          d.available_start_times.length > 0 &&
          d.is_available,
      );
      if (firstAvailableDate) {
        return selectTime(moment(firstAvailableDate.available_start_times[0]));
      }
      // If nothing is available, select the start time on the first date in view
      return selectTime(
        moment(
          `${availableDatesToShow?.[0].date} ${availableDatesToShow?.[0].start_time}`,
          'YYYY-MM-DD hh:mm A',
        ),
      );
    }
  };

  const updateAppointmentProperties = (propsToUpdate) => {
    const updatedEvents = updatePropertiesHelper(
      propsToUpdate,
      events,
      'new',
      timeIsUnavailable,
      timeZone,
    );
    setEvents(updatedEvents);
  };

  // Make appointment editable only once compatible dates are loaded, otherwise event gets duplicated
  useEffect(() => {
    updateAppointmentProperties({
      startEditable:
        !isLoadingAdditionalDates && !isLoadingAdditionalDispatchInfo,
    });
  }, [isLoadingAdditionalDates, isLoadingAdditionalDispatchInfo]);

  const calculateEndTime = (start) => {
    if (start) {
      return moment(start)
        .add(total_labor_hours * 60, 'minutes')
        .format();
    }
    return null;
  };
  // Schedule is an object returned from the /get-compatible-dates response
  const validateTimeOnSchedule = (schedule, momentDatetime) => {
    const isTimeAvailable =
      schedule.is_available &&
      isTimeWithinAvailabilityRange(
        momentDatetime,
        calculateEndTime,
        availableTimeEvents,
      );
    if (isTimeAvailable) {
      setConflictReason(null);
    } else {
      const unavailableReason =
        schedule?.unavailable_reason ||
        `${momentDatetime.tz(timeZone).format('h:mm a')} - Time Not Free`;
      setConflictReason(unavailableReason);
    }
    setTimeIsUnavailable(!isTimeAvailable);
    const startTime = momentDatetime.format();
    const endTime = calculateEndTime(startTime);
    const renderedBorderColor = borderColor(
      startTime,
      isTimeAvailable,
      allCompatibleDates,
      scheduleData?.service_vehicle_id,
      theme,
      is_using_time_blocks,
      timeZone,
    );
    updateAppointmentProperties({
      start: startTime,
      end: endTime,
      textColor: isTimeAvailable ? theme.palette.info.dark : '#cd6163',
      borderColor: renderedBorderColor,
      className: [
        renderedBorderColor === 'green'
          ? classes.bolderBorder
          : classes.thinBorder,
      ],
      extendedProps: {
        bufferTimeMinutes: schedule.buffer_time_minutes,
      },
    });
  };

  const selectTime = (momentDatetime, revert = () => {}) => {
    const chosenDate = momentDatetime.format('YYYY-MM-DD');
    const matchingSchedule = availableDatesToShow?.find(
      (si) => si?.date === chosenDate,
    );
    if (matchingSchedule) {
      validateTimeOnSchedule(matchingSchedule, momentDatetime);
      setScheduleData((prev) => ({
        ...prev,
        selected_date: chosenDate,
      }));
      setSelectedDate(chosenDate);
      setSelectedTimeSlot(momentDatetime.tz(timeZone).format('h:mm a'));
      onRruleChange('dtstart', new Date(`${chosenDate}T00:00:00`));
    } else {
      revert();
    }
  };

  const getBeginningOfWeek = (shownDate = undefined) => {
    return moment(shownDate)
      .startOf('week')
      .format('YYYY-MM-DD');
  };
  const getEndOfNextWeek = (shownDate = undefined) => {
    return moment(shownDate)
      .add(1, 'week')
      .endOf('week')
      .format('YYYY-MM-DD');
  };

  const fetchDispatchInfo = (startDate, endDate) => {
    setIsLoadingAdditionalDispatchInfo(true);
    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    return apiGet('/get-dispatch-info', params)
      .then((response) => {
        if (!response || response.status === 'error') {
          notify('Failed to fetch schedule data', 'warning');
          return [];
        }
        return response;
      })
      .catch((error) => {
        console.log('Error: ', error);
        notify('Failed to fetch schedule data', 'warning');
      })
      .finally(() => {
        setIsLoadingDispatchInfo(false);
        setIsLoadingAdditionalDispatchInfo(false);
      });
  };

  const getEvents = ({ startStr }) => {
    const startDateString = startStr.split('T')[0];
    const startDate = getBeginningOfWeek(startDateString);
    const endDate = getEndOfNextWeek(startDateString);
    // If the first available datetime is in a future week, getEvents gets called twice and leads to a race condition. Store latest promise in ref to store dispatch info for the later call.
    // https://sebastienlorber.com/handling-api-request-race-conditions-in-react#cancelling-former-api-calls
    const currentDispatchInfoPromise = fetchDispatchInfo(startDate, endDate);
    lastDispatchInfoPromise.current = currentDispatchInfoPromise;
    currentDispatchInfoPromise.then((response) => {
      // Set time zone regardless of which call it's from since we do not support operations in multiple timezones yet
      if (response.time_zone && response.time_zone !== timeZone) {
        setTimeZone(response.time_zone);
      }
      if (currentDispatchInfoPromise === lastDispatchInfoPromise.current) {
        const fullSchedule = [...dispatchInfo, ...response.schedule];
        const allUniqueSchedules = _.uniqBy(fullSchedule, (item) =>
          [item.schedule_instance_id, item.service_vehicle_id].join(),
        );
        setDispatchInfo(allUniqueSchedules);
      }
    });
    if (!isRecurring) {
      selectFirstAvailableTime(
        moment(startDateString).startOf('week'),
        moment(startDateString).endOf('week'),
      );
    }
    getCompatibleDates(startDate, endDate);
  };

  // Formatted to pass into <CalendarAppointment>
  const formatNewAppointment = (eventInfo) => {
    return {
      start_time: eventInfo.start,
      end_time: eventInfo.end,
      buffer_time_minutes: eventInfo.extendedProps?.bufferTimeMinutes || 0,
      drive_time_minutes: 0,
      total_labor_hours: moment(eventInfo.end).diff(
        eventInfo.start,
        'hours',
        true,
      ),
      location_type,
    };
  };

  // Searches for the available_start_time that comes right before a specified time
  const findStartTimeBeforeCutoff = (cutoffTime, availableStartTimes) => {
    return availableStartTimes.reduce((cutoffStartTime, curr) => {
      const diff = moment(cutoffTime).diff(curr);
      const currentDiff = moment(cutoffTime).diff(cutoffStartTime);
      if (diff > 0 && diff < currentDiff) {
        return moment(curr);
      }
      return moment(cutoffStartTime);
    }, moment(cutoffTime).diff(availableStartTimes[0]));
  };

  // When compatible dates loads or selected sv changes, filter all compatible dates to the ones that are available and for the selected service vehicle
  useEffect(() => {
    const newDatesForServiceVehicle = allCompatibleDates.filter(
      (d) => d.service_vehicle_id === scheduleData?.service_vehicle_id,
    );
    newDatesForServiceVehicle.sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );
    const newAvailableTimes = [];
    const startTimeSlotEvents = calcTimeSlotTimes(
      newDatesForServiceVehicle,
      scheduleData?.service_vehicle_id,
      classes,
    );
    newDatesForServiceVehicle.forEach((dateObj) => {
      if (dateObj.is_available && dateObj.available_time_ranges) {
        dateObj.available_time_ranges.forEach((timeRange, idx) => {
          // Subtract buffer time from tail end of time ranges to account for buffer time after new appointment, except for the last time range in a day so the available range extends to the sv's availability end time
          const isLastRangeOfDay =
            idx === dateObj.available_time_ranges.length - 1;
          const currentEndTime = new Date(timeRange[1]);
          const newEndTime = moment(
            findStartTimeBeforeCutoff(
              currentEndTime,
              dateObj.available_start_times,
            ),
          ).tz(timeZone);
          const endTime = isLastRangeOfDay
            ? moment(timeRange[1])
                .tz(timeZone)
                .format()
            : newEndTime.add(total_labor_hours, 'hours').toDate();
          newAvailableTimes.push({
            groupId: 'availableForAppointment',
            start: moment(timeRange[0])
              .tz(timeZone)
              .format(),
            end: endTime,
            display: 'background',
            backgroundColor: 'lightgreen',
            extendedProps: {
              isLastRangeOfDay,
            },
          });
        });
      }
      // In addition to adding actual availability, also add background availability
      // for all dates with a schedule to allow for scheduling overrides
      const start = moment(dateObj.date)
        .set({ hour: 0, minute: 0 })
        .tz(timeZone, true)
        .format();
      const end = moment(dateObj.date)
        .set({ hour: 23, minute: 59 })
        .tz(timeZone, true)
        .format();
      newAvailableTimes.push({
        groupId: 'availableForAppointment',
        start,
        end,
        display: 'inverse-background',
        backgroundColor: 'rgb(128, 128, 128)',
      });
    });
    newAvailableTimes.sort((a, b) => a.start - b.start);
    setAvailableDatesToShow(newDatesForServiceVehicle);
    setAvailableTimeEvents(newAvailableTimes);
    setStartTimeSlots(startTimeSlotEvents);
    setViewVariables(newAvailableTimes, viewRange, timeZone, setViewRange);
  }, [allCompatibleDates, scheduleData?.service_vehicle_id]);

  return (
    <>
      <Box className={calendarClasses.weekViewCalendar}>
        <Spin
          indicator={<CircularProgress />}
          spinning={isLoadingCompatibleDates || isLoadingDispatchInfo}
        >
          <FullCalendar
            ref={calendarRef}
            aspectRatio="1.5"
            plugins={[timeGridPlugin, interactionPlugin, momentTimezonePlugin]}
            initialView="timeGridWeek"
            schedulerLicenseKey={process.env.REACT_APP_FULLCALENDAR_LICENSE_KEY}
            timeZone={timeZone || 'local'}
            initialDate={selectedDate}
            allDayContent={false}
            allDayText=""
            events={[...events, ...availableTimeEvents, ...startTimeSlots]}
            datesSet={getEvents}
            contentHeight="500px"
            slotDuration="00:30:00"
            snapDuration="00:15:00"
            defaultTimedEventDuration="00:30"
            eventMinHeight={30}
            expandRows
            slotMinTime="00:00:00"
            slotMaxTime="23:59:00"
            scrollTime={timeRangeMin}
            slotLabelInterval="01:00"
            weekends
            headerToolbar={{
              left: 'prev',
              center: 'title',
              right: 'today next',
            }}
            eventDrop={({ event: { startStr }, revert }) =>
              selectTime(moment(startStr), revert)
            }
            dateClick={(data) => {
              selectTime(moment(data.dateStr));
            }}
            eventContent={(e) => {
              if (
                e.event.extendedProps?.department === 'startTime' &&
                is_using_time_blocks
              ) {
                return RenderSlotLaneTime(classes);
              }
              if (e.event.display.includes('background')) return null;
              return (
                <CalendarAppointment
                  eventInfo={e}
                  appointment={
                    getAndFormatAppointment(dispatchInfo, e.event) ||
                    formatNewAppointment(e.event)
                  }
                  timeZone={timeZone}
                  timeIsUnavailable={timeIsUnavailable}
                  conflictReason={e.isDraggable ? conflictReason : null}
                />
              );
            }}
          />
        </Spin>
      </Box>
      {renderTimeZone(timeZone)}
      {renderLegend(COLOR_LEGEND)}
    </>
  );
};

export default TimeSlotCalendar;
